/** Same level display pattern as ConfigComparePage: CLUSTER | PILOT | PILOT.X | CLUSTER.X */
export function formatLevelDisplay(
  metadata: Record<string, any> | undefined,
  fallback = '—'
): string {
  if (
    !metadata ||
    (metadata.level == null &&
      metadata.levelDisplay == null &&
      (metadata as any).level_display == null)
  ) {
    return fallback;
  }
  const levelRaw = String(
    metadata?.level ?? (metadata as any)?.level_display ?? 'N/A'
  ).trim().toLowerCase();
  const displayPrefix = levelRaw === 'cluster' ? 'CLUSTER' : levelRaw === 'global' ? 'GLOBAL' : 'PILOT';
  const rawLevelDisplay =
    metadata?.levelDisplay ?? (metadata as any)?.level_display;
  const levelDisplayPart =
    rawLevelDisplay != null && String(rawLevelDisplay).trim() !== ''
      ? String(rawLevelDisplay).trim()
      : '';
  const levelDisplayPartLower = levelDisplayPart.toLowerCase();
  const isRedundant =
    levelDisplayPartLower === displayPrefix.toLowerCase() ||
    levelDisplayPartLower === levelRaw;
  return levelDisplayPart && !isRedundant
    ? `${displayPrefix}.${levelDisplayPart}`
    : displayPrefix;
}

/** Config row shape for level display (PilotConfigsPage / new GET /v1/configs response) */
export interface ConfigLevelDisplayInput {
  configLevel?: string | null;
  productType?: string | null;
  fuelType?: string | null;
  clusterReference?: string | null;
  /** Top-level level_display from backend (human-readable); used first for Config Level column */
  level_display?: string | null;
  level?: string | null;
  /** When present, level display is derived from metadata.level + metadata.level_display */
  metadata?: { level?: string; level_display?: string; levelDisplay?: string } | null;
}

export type ClusterOption = { value: string; label: string };

/**
 * Build level display string for a config row (same format as formatLevelDisplay).
 * Uses level_display when present (per backend contract); else metadata; else configLevel/productType/fuelType/clusterReference.
 */
export function getLevelDisplayForConfig(
  cfg: ConfigLevelDisplayInput,
  clusters?: ClusterOption[]
): string {
  const topLevelDisplay = (cfg?.level_display != null && String(cfg.level_display).trim() !== '')
    ? String(cfg.level_display).trim()
    : '';
  if (topLevelDisplay) {
    const lvl = String(cfg?.configLevel ?? cfg?.level ?? 'pilot').trim().toLowerCase();
    const prefix = lvl === 'cluster' ? 'CLUSTER' : lvl === 'global' ? 'GLOBAL' : 'PILOT';
    const lower = topLevelDisplay.toLowerCase();
    if (topLevelDisplay.startsWith(`${prefix}.`)) return topLevelDisplay;
    if (lower === prefix.toLowerCase() || lower === lvl) return prefix;
    return `${prefix}.${topLevelDisplay}`;
  }

  const meta = cfg?.metadata;
  const metaLevel = (meta?.level ?? (meta as any)?.level_display ?? '').trim().toLowerCase();
  const metaLevelDisplay = (meta?.level_display ?? (meta as any)?.levelDisplay ?? '').trim();

  if (metaLevelDisplay && metaLevel) {
    const prefix = metaLevel === 'cluster' ? 'CLUSTER' : metaLevel === 'global' ? 'GLOBAL' : 'PILOT';
    const partLower = metaLevelDisplay.toLowerCase();
    const isRedundant = partLower === prefix.toLowerCase() || partLower === metaLevel;
    return partLower && !isRedundant ? `${prefix}.${metaLevelDisplay}` : prefix;
  }
  if (metaLevelDisplay) {
    const prefix = metaLevel === 'cluster' ? 'CLUSTER' : metaLevel === 'global' ? 'GLOBAL' : 'PILOT';
    return `${prefix}.${metaLevelDisplay}`;
  }

  const level = String(cfg?.configLevel ?? cfg?.level ?? 'pilot').trim().toLowerCase();
  if (level === 'pilot') return 'PILOT';
  if (level === 'cluster') {
    const ref = cfg?.clusterReference != null ? String(cfg.clusterReference).trim() : '';
    const label =
      clusters && ref
        ? clusters.find((c) => (c.value || '').trim() === ref)?.label
        : undefined;
    const part = (label && label.trim()) || ref;
    return part ? `CLUSTER.${part}` : 'CLUSTER';
  }
  if (level === 'product_type') {
    const part = (cfg?.productType != null && String(cfg.productType).trim() !== '')
      ? String(cfg.productType).trim()
      : '';
    return part ? `PILOT.${part}` : 'PILOT';
  }
  if (level === 'fuel_type') {
    const part = (cfg?.fuelType != null && String(cfg.fuelType).trim() !== '')
      ? String(cfg.fuelType).trim()
      : '';
    return part ? `PILOT.${part}` : 'PILOT';
  }
  if (level === 'global') {
    return 'GLOBAL';
  }
  return 'PILOT';
}
